/**
 * Chakra Architect — top-level app (fase 4).
 *
 * Volledige run-flow via runStore: chakra-pad → confrontatie → gevecht →
 * leermoment → (sub: volgende baas | eindbaas: meesterschap) → pad. Voortgang
 * persistent via progressStore. Reisdagboek + Archief als overlays (Archief =
 * stub tot fase 5).
 */
import { useEffect, useState } from 'react';
import { useBattleStore } from '../store/battleStore';
import { useRunStore } from '../store/runStore';
import { ChakraPad } from './ChakraPad';
import { ConfrontatieScene } from './ConfrontatieScene';
import { BattleScreen } from './BattleScreen';
import { LeermomentScene } from './LeermomentScene';
import { MeesterschapScene } from './MeesterschapScene';
import { Reisdagboek } from './Reisdagboek';
import { Archief } from './Archief';
import { Codex } from './Codex';
import { Instellingen } from './Instellingen';
import { ChakraAchtergrond } from './art/ChakraAchtergrond';
import type { Origin } from '../data/types';

type Overlay = 'reisdagboek' | 'archief' | 'codex' | 'instellingen' | null;

export function CaApp() {
  const taal = useBattleStore((s) => s.taal);
  const battle = useBattleStore((s) => s.battle);
  const { fase, chakra, baasIndex, startChakra, beginGevecht, naGevecht, naLeermoment, naMeesterschap } = useRunStore();
  const [overlay, setOverlay] = useState<Overlay>(null);
  const huidigChakra = (chakra ?? 1) as Origin;

  // Dev/test: ?chakra=N start direct chakra N; ?overlay=archief|codex|reisdagboek.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const m = q.get('chakra');
    if (m) startChakra(Math.min(7, Math.max(1, parseInt(m, 10) || 1)) as Origin);
    const bt = q.get('battle'); // dev: spring direct in een gevecht (baas-index 0-3)
    if (bt !== null) { startChakra(1); useRunStore.setState({ baasIndex: Math.min(3, Math.max(0, parseInt(bt, 10) || 0)) }); beginGevecht('erkennen'); }
    const o = q.get('overlay');
    if (o === 'archief' || o === 'codex' || o === 'reisdagboek' || o === 'instellingen') setOverlay(o);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (overlay === 'reisdagboek') return <Reisdagboek taal={taal} onTerug={() => setOverlay(null)} />;
  if (overlay === 'archief') return <Archief taal={taal} onTerug={() => setOverlay(null)} />;
  if (overlay === 'codex') return <Codex taal={taal} onTerug={() => setOverlay(null)} />;
  if (overlay === 'instellingen') return <Instellingen taal={taal} onTerug={() => setOverlay(null)} />;

  if (fase === 'confrontatie') {
    return <ConfrontatieScene chakra={huidigChakra} baasIndex={baasIndex} taal={taal} onStart={beginGevecht} />;
  }
  if (fase === 'gevecht' && battle) {
    return (
      <ChakraAchtergrond chakra={huidigChakra}>
        <BattleScreen onVoltooid={naGevecht} />
      </ChakraAchtergrond>
    );
  }
  if (fase === 'leermoment') {
    return <LeermomentScene chakra={huidigChakra} baasIndex={baasIndex} taal={taal} onKlaar={naLeermoment} />;
  }
  if (fase === 'meesterschap') {
    return <MeesterschapScene chakra={huidigChakra} taal={taal} onKlaar={naMeesterschap} />;
  }

  return (
    <ChakraPad
      taal={taal}
      onReisdagboek={() => setOverlay('reisdagboek')}
      onArchief={() => setOverlay('archief')}
      onCodex={() => setOverlay('codex')}
      onInstellingen={() => setOverlay('instellingen')}
    />
  );
}
